import { useEffect, useRef } from "react";

const LABELED = [
  "ERP", "CRM", "Data Lake", "Team", "Decision", "Workflow",
  "System", "Owner", "Signal", "Process",
];

export default function LivingGraph({ className = "" }) {
  const canvasRef = useRef(null);
  const mouse = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let rafId;
    let width = 0;
    let height = 0;
    let start = null;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    const resize = () => {
      const rect = canvas.parentElement.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const NODE_COUNT = width < 768 ? 42 : 68;
    const nodes = Array.from({ length: NODE_COUNT }, (_, i) => {
      const cx = width * (0.12 + 0.76 * Math.random());
      const cy = height * (0.1 + 0.8 * Math.random());
      return {
        bx: cx,
        by: cy,
        phase: Math.random() * Math.PI * 2,
        speed: 0.12 + Math.random() * 0.22,
        amp: 10 + Math.random() * 26,
        r: 1.1 + Math.random() * 1.6,
        delay: Math.random() * 0.55,
        hub: i % 11 === 0,
        label: i % 7 === 0 ? LABELED[(i / 7) % LABELED.length | 0] : null,
      };
    });

    const onMouse = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouse.current = {
        x: (e.clientX - rect.left) / rect.width - 0.5,
        y: (e.clientY - rect.top) / rect.height - 0.5,
      };
    };
    window.addEventListener("mousemove", onMouse);

    const easeOut = (t) => 1 - Math.pow(1 - t, 3);

    const draw = (ts) => {
      if (start === null) start = ts;
      const elapsed = (ts - start) / 1000;
      const t = elapsed;
      ctx.clearRect(0, 0, width, height);

      const px = mouse.current.x * 24;
      const py = mouse.current.y * 24;

      const pts = nodes.map((n) => {
        const form = easeOut(Math.min(Math.max((t - 0.3 - n.delay) / 1.6, 0), 1));
        const x = n.bx + Math.cos(t * n.speed + n.phase) * n.amp + px * (n.hub ? 1.6 : 1);
        const y = n.by + Math.sin(t * n.speed * 0.9 + n.phase * 1.3) * n.amp + py * (n.hub ? 1.6 : 1);
        return { ...n, x, y, form };
      });

      const threshold = Math.min(width, height) * 0.22;
      for (let i = 0; i < pts.length; i++) {
        for (let j = i + 1; j < pts.length; j++) {
          const a = pts[i];
          const b = pts[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const d = Math.hypot(dx, dy);
          if (d < threshold) {
            const edgeForm = Math.min(a.form, b.form);
            const alpha = (1 - d / threshold) * 0.16 * edgeForm;
            if (alpha > 0.004) {
              ctx.strokeStyle = `rgba(255,255,255,${alpha})`;
              ctx.lineWidth = 1;
              ctx.beginPath();
              ctx.moveTo(a.x, a.y);
              ctx.lineTo(b.x, b.y);
              ctx.stroke();
            }
          }
        }
      }

      pts.forEach((n) => {
        if (n.form <= 0) return;
        if (n.hub) {
          const pulse = 0.5 + 0.5 * Math.sin(t * 1.4 + n.phase);
          ctx.fillStyle = `rgba(0,192,200,${(0.35 + 0.45 * pulse) * n.form})`;
          ctx.beginPath();
          ctx.arc(n.x, n.y, n.r + 1.8, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = `rgba(0,192,200,${0.08 * pulse * n.form})`;
          ctx.beginPath();
          ctx.arc(n.x, n.y, n.r + 10 + pulse * 6, 0, Math.PI * 2);
          ctx.fill();
        } else {
          ctx.fillStyle = `rgba(255,255,255,${0.55 * n.form})`;
          ctx.beginPath();
          ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
          ctx.fill();
        }
        if (n.label && n.form > 0.85) {
          ctx.font = "10px 'JetBrains Mono', monospace";
          ctx.fillStyle = `rgba(255,255,255,${0.32 * n.form})`;
          ctx.fillText(n.label, n.x + 8, n.y - 6);
        }
      });

      rafId = requestAnimationFrame(draw);
    };
    rafId = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", onMouse);
    };
  }, []);

  return <canvas ref={canvasRef} className={className} aria-hidden="true" />;
}
